/**
 * The "Load status" column of the ticket tables that render through DataTable — Transaction
 * Search and Amendments (ST-00218, lm-status) — and the "Load status" text column of their XLSX
 * exports. The label comes from the tenant's load-match ledger (POST
 * /api/load-matching/ticket-status, via hooks/useLoadStatuses and services/loadStatus).
 *
 *   const ls = useLoadStatusColumn(rows, allColumns, visibleColumns, setVisibleColumns);
 *   <ColumnSelector allColumns={ls.allColumns} visibleColumns={ls.visibleColumns}
 *                   onVisibleColumnsChange={ls.onVisibleColumnsChange} />
 *   <DataTable columns={ls.visibleColumns} … />
 *   const { rows } = await addLoadStatusToExport(exportRows);   // before XLSX.json_to_sheet
 *
 * The column exists only while the server answers `enabled: true` (LOAD_MATCHING shadow / on):
 * with LOAD_MATCHING=off, or before the first answer, the tables and exports are unchanged. It is
 * placed after the ticket type (else after the ticket no), visible by default, and the user can
 * hide it in the column picker. The page's own visible-column state never holds it, so a page
 * keeps its column logic as it was.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { LoadStatusCell } from '../../common/LoadStatusBadge';
import { useLoadStatuses } from '../../../hooks/useLoadStatuses';
import { fetchLoadStatuses, loadStatusText } from '../../../services/loadStatus';

/** The column's header (and its name in the column picker). */
export const LOAD_STATUS_HEADER = 'Load status';
/** The column's accessor: no row field has it, the cell reads the label by TicketUID. */
export const LOAD_STATUS_ACCESSOR = 'loadStatus';
/** The export's column name. */
export const LOAD_STATUS_EXPORT_KEY = 'Load status';
/** The column goes after the first of these that the table shows (else first). */
const ANCHORS = ['Ticket Type', 'Ticket No'] as const;

/** The part of a ticket row the column reads. */
export interface LoadStatusRow {
  TicketUID?: string | null;
}

/** A ticket row as the export writes it (every view column, by display name). */
export interface LoadStatusExportRow extends LoadStatusRow {
  [column: string]: unknown;
}

/** The react-table v7 column (DataTable renders `Cell`; ColumnSelector keys on `accessor`). */
export interface LoadStatusTableColumn {
  Header: string;
  accessor: string;
  // Method syntax on purpose: the pages' column types read their own cell props.
  Cell(props: { row?: { original?: LoadStatusRow } }): ReactNode;
}

export const isLoadStatusColumn = (col: { accessor: string }): boolean =>
  col.accessor === LOAD_STATUS_ACCESSOR;

/** `columns` with `col` placed after the ticket type (else the ticket no, else first). */
export function insertLoadStatusColumn<C extends { accessor: string }, L>(
  columns: readonly C[],
  col: L
): (C | L)[] {
  const anchor = ANCHORS.map((a) => columns.findIndex((c) => c.accessor === a)).find((i) => i >= 0);
  const at = anchor === undefined ? 0 : anchor + 1;
  return [...columns.slice(0, at), col, ...columns.slice(at)];
}

export interface LoadStatusColumns<C> {
  /** true while the server runs load matching: the column is offered. */
  enabled: boolean;
  /** Every column the picker offers (with Load status when enabled). */
  allColumns: (C | LoadStatusTableColumn)[];
  /** The columns the table shows (with Load status when enabled and not hidden). */
  visibleColumns: (C | LoadStatusTableColumn)[];
  /** For the column picker: keeps the page's own columns and the Load status choice apart. */
  onVisibleColumnsChange: (columns: (C | LoadStatusTableColumn)[]) => void;
}

/**
 * The Load status column for a table listing `rows` (the rows on screen): looks their labels up
 * and adds the column to the page's columns when the server says load matching is enabled.
 */
export function useLoadStatusColumn<C extends { accessor: string }>(
  rows: readonly LoadStatusRow[],
  allColumns: readonly C[],
  visibleColumns: readonly C[],
  setVisibleColumns: (columns: C[]) => void
): LoadStatusColumns<C> {
  const uids = useMemo(() => rows.map((r) => r.TicketUID), [rows]);
  const { enabled, statuses, loading } = useLoadStatuses(uids);
  const on = enabled === true;
  const [hidden, setHidden] = useState(false);

  const column = useMemo<LoadStatusTableColumn>(
    () => ({
      Header: LOAD_STATUS_HEADER,
      accessor: LOAD_STATUS_ACCESSOR,
      Cell: ({ row }) => {
        const uid = row?.original?.TicketUID;
        return <LoadStatusCell status={uid ? statuses[uid] : null} loading={loading} />;
      },
    }),
    [statuses, loading]
  );

  const all = useMemo(
    () => (on ? insertLoadStatusColumn(allColumns, column) : [...allColumns]),
    [on, allColumns, column]
  );
  const visible = useMemo(
    () => (on && !hidden ? insertLoadStatusColumn(visibleColumns, column) : [...visibleColumns]),
    [on, hidden, visibleColumns, column]
  );
  const onVisibleColumnsChange = useCallback(
    (columns: (C | LoadStatusTableColumn)[]) => {
      if (on) setHidden(!columns.some(isLoadStatusColumn));
      setVisibleColumns(columns.filter((c): c is C => !isLoadStatusColumn(c)));
    },
    [on, setVisibleColumns]
  );

  return { enabled: on, allColumns: all, visibleColumns: visible, onVisibleColumnsChange };
}

/** `row` with `key` = `value` placed after `afterKey` (at the end when the row lacks it). */
function withKeyAfter(
  row: LoadStatusExportRow,
  key: string,
  value: string,
  afterKey: string
): LoadStatusExportRow {
  if (!(afterKey in row)) return { ...row, [key]: value };
  const out: LoadStatusExportRow = {};
  for (const [k, v] of Object.entries(row)) {
    if (k === key) continue;
    out[k] = v;
    if (k === afterKey) out[key] = value;
  }
  return out;
}

/**
 * The export rows with a "Load status" text column after the ticket type ("Needs review —
 * Probable duplicate weighing"; blank without a ledger row), when the server runs load matching.
 * The export holds rows the screen never showed, so their labels are looked up here (batched,
 * cached). LOAD_MATCHING=off, or a failed lookup → the rows unchanged (`included: false`).
 */
export async function addLoadStatusToExport(
  rows: readonly LoadStatusExportRow[]
): Promise<{ rows: LoadStatusExportRow[]; included: boolean }> {
  if (rows.length === 0) return { rows: [...rows], included: false };
  const { enabled, statuses } = await fetchLoadStatuses(rows.map((r) => r.TicketUID));
  if (enabled !== true) return { rows: [...rows], included: false };
  return {
    rows: rows.map((r) =>
      withKeyAfter(
        r,
        LOAD_STATUS_EXPORT_KEY,
        loadStatusText(r.TicketUID ? statuses[r.TicketUID] : null),
        'Ticket Type'
      )
    ),
    included: true,
  };
}
